import { useEffect } from "react";
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { calcAge, calcPercentChange } from "@/lib/calc";
import { Section } from "../view/section";
import { CurrencyInput, FormField, FormInput } from "./form-field";
import type { InsuranceFormData } from "./types";

interface Props {
  register: UseFormRegister<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
}

export function SectionFinancialForm({ register, watch, setValue }: Props): React.ReactElement {
  const sModal = watch("sModal");
  const eModal = watch("eModal");
  const certDate = watch("certificateDate");
  const eyrBuilt = watch("eyrBuilt");
  const pctOverride = watch("percentChangeOverride");
  const ageOverride = watch("ageOverride");

  // Auto-calc % Change from S Modal / E Modal
  useEffect(() => {
    if (pctOverride) return;
    if (sModal != null && eModal != null && sModal !== 0) {
      setValue("percentChange", Math.round(calcPercentChange(sModal, eModal) * 100) / 100);
    }
  }, [sModal, eModal, pctOverride, setValue]);

  // Auto-calc Age from certificate date - EYR Built
  useEffect(() => {
    if (ageOverride) return;
    if (certDate && eyrBuilt != null) {
      setValue("age", calcAge(certDate, eyrBuilt));
    }
  }, [certDate, eyrBuilt, ageOverride, setValue]);

  return (
    <Section id="financial" title="Financial Configuration">
      <p className="text-xs text-muted-foreground mb-4">
        Changes here trigger recalculation across all line items below.
      </p>
      <div className="grid gap-4 md:grid-cols-2 mb-3">
        <div className="grid grid-cols-3 gap-3">
          <FormField label="S Modal" htmlFor="sModal">
            <CurrencyInput id="sModal" value={sModal} onChange={(v) => setValue("sModal", v, { shouldDirty: true })} />
          </FormField>
          <FormField label="E Modal" htmlFor="eModal">
            <CurrencyInput id="eModal" value={eModal} onChange={(v) => setValue("eModal", v, { shouldDirty: true })} />
          </FormField>
          <FormField label="% Change" htmlFor="percentChange" hint={pctOverride ? "manual" : "auto"}>
            <FormInput
              id="percentChange"
              type="number"
              step="0.01"
              {...register("percentChange", {
                valueAsNumber: true,
                onChange: () => setValue("percentChangeOverride", true),
              })}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <FormField label="Age" htmlFor="age" hint={ageOverride ? "manual" : "auto"}>
            <FormInput
              id="age"
              type="number"
              {...register("age", {
                valueAsNumber: true,
                onChange: () => setValue("ageOverride", true),
              })}
            />
          </FormField>
          <FormField label="Fees %" htmlFor="feesPercentage">
            <FormInput
              id="feesPercentage"
              type="number"
              step="0.01"
              {...register("feesPercentage", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Demolition %" htmlFor="demolitionPercentage">
            <FormInput
              id="demolitionPercentage"
              type="number"
              step="0.01"
              {...register("demolitionPercentage", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="GST Rate %" htmlFor="gst">
            <FormInput id="gst" type="number" step="0.01" {...register("gst", { valueAsNumber: true })} />
          </FormField>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-medium mb-3">Reinstatement Period & Inflation</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <FormField label="First Year Mo" htmlFor="firstYearMonths">
            <FormInput id="firstYearMonths" type="number" {...register("firstYearMonths", { valueAsNumber: true })} />
          </FormField>
          <FormField label="Demo/Plan Mo" htmlFor="inflationDemoPlanMonths">
            <FormInput
              id="inflationDemoPlanMonths"
              type="number"
              {...register("inflationDemoPlanMonths", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Construction Mo" htmlFor="inflationConstructionMonths">
            <FormInput
              id="inflationConstructionMonths"
              type="number"
              {...register("inflationConstructionMonths", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Year 1 %" htmlFor="inflationYear1">
            <FormInput
              id="inflationYear1"
              type="number"
              step="0.01"
              {...register("inflationYear1", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Year 2 %" htmlFor="inflationYear2">
            <FormInput
              id="inflationYear2"
              type="number"
              step="0.01"
              {...register("inflationYear2", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Year 3 %" htmlFor="inflationYear3">
            <FormInput
              id="inflationYear3"
              type="number"
              step="0.01"
              {...register("inflationYear3", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Year 4 %" htmlFor="inflationYear4">
            <FormInput
              id="inflationYear4"
              type="number"
              step="0.01"
              {...register("inflationYear4", { valueAsNumber: true })}
            />
          </FormField>
        </div>
      </div>
    </Section>
  );
}
